/**
 * src/services/permissionTemplateService.js
 *
 * Gabarits de permissions — QUELLES PAGES un gabarit ouvre.
 *
 * La base fait deja le gros du travail : RLS en lecture pour les membres de
 * l'organisation, ecriture reservee au role 'admin', et un trigger qui REFUSE
 * la suppression d'un gabarit systeme. Ce service ne re-implemente rien de
 * tout cela — il traduit les refus en messages lisibles et ajoute la seule
 * garde que la base ne peut pas connaitre : ne pas enfermer le dernier
 * administrateur dehors.
 */

import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

export const PAGE_USERS = 'app.users';

/** Motifs de refus, rendus lisibles par l'appelant via i18n. */
export const TEMPLATE_BLOCKED = {
  SYSTEM_TEMPLATE: 'system_template',
  IN_USE: 'in_use',
  LAST_ADMIN_USERS_PAGE: 'last_admin_users_page',
};

/** Les administrateurs actifs de l'organisation, avec leur gabarit. */
const activeAdmins = async (orgId) => {
  const { data, error } = await supabase
    ?.from('user_profiles')
    ?.select('id, permission_template_id')
    ?.eq('organization_id', orgId)
    ?.eq('role', 'admin')
    ?.eq('is_active', true);
  if (error) throw error;
  return data || [];
};

/**
 * Les gabarits de l'organisation, chacun avec le nombre d'utilisateurs qui le
 * portent. Le compte sert deux fois : l'afficher, et avertir avant de
 * supprimer un gabarit encore attribue.
 */
export const listTemplates = async () => {
  try {
    const orgId = await getCurrentOrgId();

    const [tplRes, usersRes] = await Promise.all([
      supabase
        ?.from('permission_templates')
        ?.select('id, name, pages, is_system')
        ?.eq('organization_id', orgId)
        ?.order('is_system', { ascending: false })
        ?.order('name', { ascending: true }),
      supabase
        ?.from('user_profiles')
        ?.select('permission_template_id')
        ?.eq('organization_id', orgId),
    ]);

    if (tplRes?.error) throw tplRes?.error;

    const counts = {};
    (usersRes?.data || [])?.forEach((u) => {
      const id = u?.permission_template_id;
      if (id) counts[id] = (counts?.[id] || 0) + 1;
    });

    const data = (tplRes?.data || [])?.map((tpl) => ({
      ...tpl,
      pages: Array.isArray(tpl?.pages) ? tpl?.pages : [],
      userCount: counts?.[tpl?.id] || 0,
    }));

    return { data, error: null };
  } catch (error) {
    console.error('permissionTemplateService.listTemplates error:', error);
    return { data: [], error };
  }
};

export const createTemplate = async (name, pages = []) => {
  try {
    const orgId = await getCurrentOrgId();
    const { data, error } = await supabase
      ?.from('permission_templates')
      ?.insert({ organization_id: orgId, name: name?.trim(), pages, is_system: false })
      ?.select('id, name, pages, is_system')
      ?.single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('permissionTemplateService.createTemplate error:', error);
    return { data: null, error };
  }
};

/** Copie d'un gabarit. La copie n'est JAMAIS systeme, meme depuis un systeme. */
export const duplicateTemplate = async (template, newName) =>
  createTemplate(newName, Array.isArray(template?.pages) ? [...template?.pages] : []);

/**
 * Renommer et/ou reecrire la liste des pages.
 *
 * GARDE ANTI-ENFERMEMENT : retirer app.users d'un gabarit porte par le dernier
 * administrateur actif lui fermerait la gestion des utilisateurs, sans aucun
 * moyen de revenir en arriere. La base ne peut pas voir ce lien — elle ne sait
 * pas qui porte quel gabarit au moment de l'ecriture.
 */
export const updateTemplate = async (templateId, { name, pages }) => {
  try {
    const orgId = await getCurrentOrgId();

    if (Array.isArray(pages) && !pages?.includes(PAGE_USERS)) {
      const admins = await activeAdmins(orgId);
      const carriedByLastAdmin =
        admins?.length <= 1 && admins?.[0]?.permission_template_id === templateId;
      if (carriedByLastAdmin) {
        return { data: null, error: null, blocked: TEMPLATE_BLOCKED?.LAST_ADMIN_USERS_PAGE };
      }
    }

    const payload = {};
    if (name !== undefined) payload.name = name?.trim();
    if (pages !== undefined) payload.pages = pages;

    const { data, error } = await supabase
      ?.from('permission_templates')
      ?.update(payload)
      ?.eq('id', templateId)
      ?.eq('organization_id', orgId)
      ?.select('id, name, pages, is_system')
      ?.single();

    if (error) throw error;
    return { data, error: null, blocked: null };
  } catch (error) {
    console.error('permissionTemplateService.updateTemplate error:', error);
    return { data: null, error, blocked: null };
  }
};

/**
 * Supprime un gabarit.
 *
 * Deux refus AVANT d'appeler la base, pour ne jamais montrer une erreur
 * Postgres brute : un gabarit systeme, et un gabarit encore attribue. Le
 * trigger reste le filet — s'il refuse quand meme, on le traduit aussi.
 */
export const deleteTemplate = async (template) => {
  try {
    if (template?.is_system) {
      return { error: null, blocked: TEMPLATE_BLOCKED?.SYSTEM_TEMPLATE };
    }
    if ((template?.userCount || 0) > 0) {
      return { error: null, blocked: TEMPLATE_BLOCKED?.IN_USE };
    }

    const orgId = await getCurrentOrgId();
    const { error } = await supabase
      ?.from('permission_templates')
      ?.delete()
      ?.eq('id', template?.id)
      ?.eq('organization_id', orgId);

    if (error) {
      // Filet : le trigger a refuse. On rend un motif, jamais le texte brut.
      const msg = (error?.message || '')?.toLowerCase();
      if (msg?.includes('system')) {
        return { error: null, blocked: TEMPLATE_BLOCKED?.SYSTEM_TEMPLATE };
      }
      throw error;
    }
    return { error: null, blocked: null };
  } catch (error) {
    console.error('permissionTemplateService.deleteTemplate error:', error);
    return { error, blocked: null };
  }
};

/**
 * Remet un gabarit SYSTEME a sa configuration d'origine.
 *
 * Tout se decide en base : public.reset_system_template() est SECURITY DEFINER
 * et refuse elle-meme un gabarit non systeme, une autre organisation, ou un
 * appelant non administrateur — en levant un message francais a chaque fois.
 * Ce service ne re-verifie rien : il transmet, puis remonte CE message.
 *
 * On distingue deux natures d'echec :
 *   - P0001 (RAISE EXCEPTION du plpgsql) : le message VIENT de la fonction,
 *     il est ecrit pour un humain, on l'affiche tel quel.
 *   - tout le reste (fonction absente, reseau, droits) : message technique,
 *     on ne le montre pas et on rend un motif generique.
 */
export const resetSystemTemplate = async (templateId) => {
  try {
    // PostgrestBuilder est un PromiseLike sans .catch() : await dans try/catch.
    const { error } = await supabase?.rpc('reset_system_template', {
      p_template_id: templateId,
    });

    if (error) {
      if (error?.code === 'P0001' && error?.message) {
        return { error: null, message: error?.message };
      }
      throw error;
    }
    return { error: null, message: null };
  } catch (error) {
    console.error('permissionTemplateService.resetSystemTemplate error:', error);
    return { error, message: null };
  }
};

export default {
  listTemplates,
  createTemplate,
  duplicateTemplate,
  updateTemplate,
  deleteTemplate,
  resetSystemTemplate,
  TEMPLATE_BLOCKED,
  PAGE_USERS,
};
